import React, { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Typography } from '@mui/material';
import { ArrowRight, ChevronDown, Pencil, RefreshCw } from 'lucide-react';
import { PersonCell, StatusPill } from '../../../components/ui';
import type { StatusTone } from '../../../components/ui';
import type { Employee } from '../../../store/slices/employeeSlice';
import { sbg } from '../../../theme/sbgTokens';
import { formatCount, MISSING } from '../../../utils/format';
import { ACCESS_TAB_LABEL, firstSentence } from './issueGuide';
import type { IssueGroup, IssueGroupPerson } from './issueGuide';

/** People shown per group before "Show all". */
const PREVIEW_COUNT = 6;

/**
 * Issue rows lay out against the accordion's own width, not the viewport: the
 * list can sit in a narrow column on a wide screen, and a fixed person track
 * beside a 1fr detail track squeezes the detail to nothing there.
 */
const ISSUE_CONTAINER = 'setup-issue';
const ROW_WIDE = `@container ${ISSUE_CONTAINER} (min-width: 720px)`;
const BAR_WIDE = `@container ${ISSUE_CONTAINER} (min-width: 560px)`;

/** PersonCell's avatar (32px) plus its gap (10px): lines the detail up under the name. */
const PERSON_TEXT_INSET = 5.25;

export interface FixActions {
  onEdit: (employee: Employee) => void;
  onSync: () => void;
  isSyncing: boolean;
  /** Search string that opens the access tab, keeping other params. */
  accessSearch: string;
}

export const displayName = (employee: Employee) => (employee.name || '').trim() || 'Unnamed employee';

export const EditEmployeeButton: React.FC<{
  employee: Employee;
  onEdit: (employee: Employee) => void;
  /** Visible label; the accessible name always names the employee. */
  label?: string;
}> = ({ employee, onEdit, label = 'Edit employee' }) => (
  <Button
    variant="text"
    size="small"
    startIcon={<Pencil />}
    aria-label={`Edit employee ${displayName(employee)}`}
    onClick={(event) => {
      event.stopPropagation();
      onEdit(employee);
    }}
    sx={{ flexShrink: 0 }}
  >
    {label}
  </Button>
);

export const SyncButton: React.FC<{
  onSync: () => void;
  isSyncing: boolean;
  size?: 'small' | 'medium';
  variant?: 'contained' | 'outlined' | 'text';
}> = ({ onSync, isSyncing, size = 'small', variant = 'outlined' }) => (
  <Button
    variant={variant}
    size={size}
    onClick={onSync}
    disabled={isSyncing}
    aria-busy={isSyncing || undefined}
    startIcon={isSyncing ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : <RefreshCw />}
  >
    {isSyncing ? 'Syncing…' : 'Sync manager links'}
  </Button>
);

/* ------------------------------------------------------------------------ */
/* One affected person                                                      */
/* ------------------------------------------------------------------------ */

const PersonRow: React.FC<{ person: IssueGroupPerson; specific: boolean; onEdit: (employee: Employee) => void }> = ({
  person: { employee, issue },
  specific,
  onEdit,
}) => {
  const placement = [employee.department?.name, employee.teamName].filter(Boolean).join(' · ') || MISSING;
  return (
    <Box
      component="li"
      sx={{
        display: 'grid',
        // Stacked by default; three tracks only once the list itself is wide
        // enough to give the detail a readable measure.
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gridTemplateAreas: '"person action" "detail detail"',
        alignItems: 'center',
        columnGap: 2,
        rowGap: 0.5,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${sbg.border.subtle}`,
        [ROW_WIDE]: {
          gridTemplateColumns: 'minmax(0, 240px) minmax(0, 1fr) auto',
          gridTemplateAreas: '"person detail action"',
        },
      }}
    >
      <PersonCell
        sx={{ gridArea: 'person' }}
        name={displayName(employee)}
        avatarName={employee.name}
        secondary={(employee.email || '').trim() || 'No email'}
      />
      <Typography
        variant="body2"
        component="p"
        sx={{
          gridArea: 'detail',
          m: 0,
          minWidth: 0,
          overflowWrap: 'anywhere',
          color: specific ? sbg.text.primary : sbg.text.secondary,
          pl: PERSON_TEXT_INSET,
          [ROW_WIDE]: { pl: 0 },
        }}
      >
        {specific ? firstSentence(issue.detail) : placement}
      </Typography>
      <Box sx={{ gridArea: 'action', justifySelf: 'end' }}>
        <EditEmployeeButton employee={employee} onEdit={onEdit} />
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------------ */
/* One issue group                                                          */
/* ------------------------------------------------------------------------ */

const IssueAccordion: React.FC<{
  group: IssueGroup;
  expanded: boolean;
  onToggle: (open: boolean) => void;
  actions: FixActions;
}> = ({ group, expanded, onToggle, actions }) => {
  const [showAll, setShowAll] = useState(false);
  const baseId = useId();
  const { guide, people } = group;
  const visible = showAll ? people : people.slice(0, PREVIEW_COUNT);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, open) => onToggle(open)}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary
        id={`${baseId}-header`}
        aria-controls={`${baseId}-content`}
        expandIcon={<ChevronDown size={18} aria-hidden="true" />}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%', minWidth: 0, pr: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: 0.25 }}>
            <Typography variant="labelMd" component="span" sx={{ color: sbg.text.primary }}>
              {group.label}
            </Typography>
            <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
              {guide.summary}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            component="span"
            sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', pt: '1px' }}
          >
            {formatCount(people.length, 'employee')}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, containerType: 'inline-size', containerName: ISSUE_CONTAINER }}>
        <Box
          sx={{
            mx: 2,
            mb: 1.5,
            px: 1.5,
            py: 1.5,
            bgcolor: sbg.bg.surfaceSubtle,
            borderRadius: sbg.radius.md,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1.5,
            [BAR_WIDE]: { flexDirection: 'row', alignItems: 'center' },
          }}
        >
          <Typography variant="body2" component="p" sx={{ color: sbg.text.primary, flex: 1, m: 0 }}>
            <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary }}>
              How to fix:{' '}
            </Typography>
            {guide.fix}
          </Typography>
          {/* Text buttons: the page header holds the one loud sync action. */}
          {guide.fixKind === 'sync' && <SyncButton variant="text" onSync={actions.onSync} isSyncing={actions.isSyncing} />}
          {guide.fixKind === 'access' && (
            <Button
              variant="text"
              size="small"
              component={RouterLink}
              to={{ search: actions.accessSearch }}
              endIcon={<ArrowRight />}
              sx={{ flexShrink: 0 }}
            >
              Open {ACCESS_TAB_LABEL}
            </Button>
          )}
        </Box>
        <Box component="ul" aria-label={`Employees with “${group.label}”`} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {visible.map((person) => (
            <PersonRow key={person.employee._id} person={person} specific={Boolean(guide.specific)} onEdit={actions.onEdit} />
          ))}
        </Box>
        {people.length > PREVIEW_COUNT && (
          <Box sx={{ borderTop: `1px solid ${sbg.border.subtle}`, px: 1, py: 1 }}>
            <Button variant="text" size="small" onClick={() => setShowAll((value) => !value)}>
              {showAll ? 'Show fewer' : `Show all ${formatCount(people.length, 'employee')}`}
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

/* ------------------------------------------------------------------------ */
/* Sections by severity                                                     */
/* ------------------------------------------------------------------------ */

const IssueSection: React.FC<{
  title: string;
  description: string;
  pill: { tone: StatusTone; label: string };
  groups: IssueGroup[];
  isExpanded: (key: string) => boolean;
  onToggle: (key: string, open: boolean) => void;
  actions: FixActions;
}> = ({ title, description, pill, groups, isExpanded, onToggle, actions }) => {
  const headingId = useId();
  return (
    <Box component="section" aria-labelledby={headingId} sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography id={headingId} variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
            {title}
          </Typography>
          <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
          <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
            {formatCount(groups.length, 'issue')}
          </Typography>
        </Box>
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.5 }}>
          {description}
        </Typography>
      </Box>
      <Box>
        {groups.map((group) => (
          <IssueAccordion
            key={group.key}
            group={group}
            expanded={isExpanded(group.key)}
            onToggle={(open) => onToggle(group.key, open)}
            actions={actions}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * The issue view: critical issues ("Fix first") above warnings ("Fix when
 * you can"), one accordion per issue with what it breaks, how to fix it, and
 * everyone affected. The largest critical group opens by default.
 */
const IssueGroups: React.FC<{ groups: IssueGroup[]; actions: FixActions }> = ({ groups, actions }) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const critical = groups.filter((group) => group.severity === 'critical');
  const warning = groups.filter((group) => group.severity !== 'critical');
  const defaultKey = (critical[0] ?? warning[0])?.key;
  const isExpanded = (key: string) => expanded[key] ?? key === defaultKey;
  const onToggle = (key: string, open: boolean) => setExpanded((prev) => ({ ...prev, [key]: open }));

  return (
    <Box sx={{ display: 'grid', gap: 4, minWidth: 0 }}>
      {critical.length > 0 && (
        <IssueSection
          title="Fix first"
          description="These stop time off, approvals or emails from reaching the right person."
          pill={{ tone: 'danger', label: 'Broken' }}
          groups={critical}
          isExpanded={isExpanded}
          onToggle={onToggle}
          actions={actions}
        />
      )}
      {warning.length > 0 && (
        <IssueSection
          title="Fix when you can"
          description="These work today but break quietly when names drift or people leave."
          pill={{ tone: 'warning', label: 'Partial' }}
          groups={warning}
          isExpanded={isExpanded}
          onToggle={onToggle}
          actions={actions}
        />
      )}
    </Box>
  );
};

export default IssueGroups;
